import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "../../lib/api";
import { goBooking } from "../../lib/promo";
import { useLang, pick } from "../../lib/i18n";
import CaptionedImage from "../CaptionedImage";

export default function Villas({ villas, bookingUrl, factsheetUrl }) {
  const { t, lang } = useLang();

  const onReserve = (villa) => {
    // Villa-specific deep link (e.g. Exely room-type) wins; opens embedded /book page on our domain
    const target = villa?.booking_url || bookingUrl;
    if (!target) {
      toast.info(t("book.notConfigured"));
      return;
    }
    goBooking(target);
  };

  return (
    <section id="villas" data-testid="villas-section" className="py-24 sm:py-32 bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-16 sm:mb-24">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="text-xs uppercase tracking-[0.3em] font-sans font-semibold text-[#9A7B66] mb-5"
            >
              {t("villas.eyebrow")}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl"
            >
              {t("villas.title")}
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-sm"
          >
            <p className="text-sm text-[#A0988E] font-sans leading-relaxed">{t("villas.intro")}</p>
            {factsheetUrl && (
              <a
                data-testid="villas-factsheet-button"
                href={mediaUrl(factsheetUrl)}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#9A7B66]/35 text-[#9A7B66] text-[12px] font-sans font-semibold uppercase tracking-[0.12em] hover:bg-[#9A7B66] hover:text-white transition-all duration-300"
              >
                <FileDown className="w-4 h-4" />
                {t("factsheet.download")}
              </a>
            )}
          </motion.div>
        </div>

        <div className="space-y-20 sm:space-y-28">
          {(villas || []).map((villa, i) => (
            <motion.article
              key={villa.id || i}
              data-testid={`villa-card-${i}`}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                i % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
            >
              <div className="lg:col-span-7 [direction:ltr]">
                <motion.div
                  initial={{ clipPath: "inset(10% 10% 10% 10%)" }}
                  whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="rounded-md overflow-hidden shadow-xl group"
                >
                  <CaptionedImage
                    testId={`villa-image-${i}`}
                    src={mediaUrl(villa.image_url)}
                    alt={villa.image_alt || pick(villa, "name", lang)}
                    title={villa.image_title || pick(villa, "name", lang)}
                    description={villa.image_description || pick(villa, "description", lang)}
                    caption={villa.image_caption || ""}
                    focalPoint={villa.image_focal_point || "50% 0%"}
                    className="w-full h-[320px] sm:h-[480px]"
                    imageClassName="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  />
                </motion.div>
              </div>
              <div className="lg:col-span-5 [direction:ltr]">
                <span className="font-mono text-xs text-[#9A7B66] tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 font-serif font-medium text-[#2C2825] text-2xl sm:text-3xl leading-snug">
                  {pick(villa, "name", lang)}
                </h3>
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#9A7B66] font-sans font-semibold">
                  {pick(villa, "specs", lang)}
                </p>
                <p className="mt-5 text-base text-[#6E675F] font-sans leading-relaxed">{pick(villa, "description", lang)}</p>
                <div className="mt-7 flex items-center justify-between border-t border-[#D3C8B9] pt-6">
                  <div>
                    <span className="text-xs uppercase tracking-[0.18em] text-[#A0988E] font-sans">{t("villas.from")}</span>
                    <div className="font-serif text-3xl text-[#2C2825]">
                      {villa.price}
                      <span className="text-sm text-[#A0988E] font-sans"> {t("villas.perNight")}</span>
                    </div>
                  </div>
                  <button
                    data-testid={`villa-reserve-button-${i}`}
                    onClick={() => onReserve(villa)}
                    className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#9A7B66] text-[#9A7B66] text-[12px] font-sans font-semibold uppercase tracking-[0.12em] hover:bg-[#9A7B66] hover:text-[#FBF9F5] transition-all duration-300"
                  >
                    {t("villas.reserve")}
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
                <Link
                  data-testid={`villa-detail-link-${i}`}
                  to={`/villa/${villa.id}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-sans font-semibold uppercase tracking-[0.14em] text-[#9A7B66] hover:text-[#826552] transition-colors"
                >
                  {t("villa.detail")}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
